// Add members (architecture sprint-12 §9.1, F1, Decision 1). Everyone who manages members gets one "Add by email"
// box: an exact match adds the user, anything else becomes an invite carrying this project grant. Below it, the
// "Add people who logged time here" suggestions (each Add calls by-email with that email). Admins also keep
// the sprint 11 multi-select of active users. Supervisors never see the user directory.
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAddByEmail, useAddMembers, useMemberSuggestions } from '@/hooks/use-project-members';
import { useAdminUsers } from '@/hooks/use-users';
import { ApiClientError } from '@/lib/api';
import { formatDurationHm } from '@/lib/format';
import type { AddByEmailResult, ProjectRole } from '@/types/settings';

export const INVITE_PENDING_INFO = "They already have a pending invite. They'll join this project when they accept it.";
const GENERIC_ERROR = 'Something went wrong. Please try again.';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface AddMembersDialogProps {
  projectId: string;
  isAdmin: boolean;
  existing: ReadonlySet<string>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** An invite was created: the parent closes this dialog and reveals the link once. */
  onInvited: (link: string, email: string, expiresAt: string) => void;
}

function RoleSelect({ id, value, onChange }: { id: string; value: ProjectRole; onChange: (r: ProjectRole) => void }): JSX.Element {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value === 'supervisor' ? 'supervisor' : 'member')}>
      <option value="member">Member</option>
      <option value="supervisor">Supervisor</option>
    </NativeSelect>
  );
}

function CheckboxRow({ id, checked, onChange, label, detail }: { id: string; checked: boolean; onChange: (v: boolean) => void; label: string; detail?: string }): JSX.Element {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-accent">
      <input id={id} type="checkbox" className="h-4 w-4 accent-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
      {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
    </label>
  );
}

/** The Admin-only multi-select of active users (sprint 11, kept). */
function AdminUserPicker({ projectId, existing, role, onAdded }: { projectId: string; existing: ReadonlySet<string>; role: ProjectRole; onAdded: () => void }): JSX.Element {
  const users = useAdminUsers('active');
  const add = useAddMembers(projectId);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const choices = (users.data?.items ?? []).filter((u) => !existing.has(u.id));

  function submit(): void {
    add.mutate(
      { userIds: [...picked], role },
      {
        onSuccess: (res) => {
          toast.success(res.added.length === 1 ? 'Member added.' : `${res.added.length} members added.`);
          setPicked(new Set());
          onAdded();
        },
      },
    );
  }

  return (
    <section className="space-y-2" aria-labelledby="add-members-users">
      <p id="add-members-users" className="text-xs font-medium text-muted-foreground">
        Active users
      </p>
      {users.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : choices.length === 0 ? (
        <p className="text-sm text-muted-foreground">Everyone is already a member.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto" role="group" aria-label="People">
          {choices.map((u) => (
            <CheckboxRow
              key={u.id}
              id={`add-${u.id}`}
              label={u.name}
              detail={u.email}
              checked={picked.has(u.id)}
              onChange={(v) =>
                setPicked((prev) => {
                  const next = new Set(prev);
                  if (v) next.add(u.id);
                  else next.delete(u.id);
                  return next;
                })
              }
            />
          ))}
        </div>
      )}
      <Button variant="outline" size="sm" onClick={submit} disabled={picked.size === 0 || add.isPending}>
        {add.isPending ? 'Adding…' : picked.size > 1 ? `Add ${picked.size} people` : 'Add selected'}
      </Button>
    </section>
  );
}

export function AddMembersDialog({ projectId, isAdmin, existing, open, onOpenChange, onInvited }: AddMembersDialogProps): JSX.Element {
  const addByEmail = useAddByEmail(projectId);
  const suggestions = useMemberSuggestions(projectId, open);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectRole>('member');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  function reset(): void {
    setEmail('');
    setError(null);
    setInfo(null);
  }

  function handleResult(result: AddByEmailResult): void {
    switch (result.outcome) {
      case 'added':
        toast.success(`${result.member.user.name} added.`);
        setEmail('');
        return;
      case 'already_member':
        toast.success(`${result.member.user.name} is already a member.`);
        setEmail('');
        return;
      case 'invited':
        reset();
        onInvited(result.link, result.invite.email, result.invite.expiresAt);
        return;
      default:
        setEmail('');
        setInfo(INVITE_PENDING_INFO);
    }
  }

  function send(address: string): void {
    setError(null);
    setInfo(null);
    setPendingEmail(address);
    addByEmail.mutate(
      { email: address, role },
      {
        onSuccess: handleResult,
        onError: (err) => setError(err instanceof ApiClientError ? err.message : GENERIC_ERROR),
        onSettled: () => setPendingEmail(null),
      },
    );
  }

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(address)) {
      setError('Enter a valid email address');
      return;
    }
    send(address);
  }

  const suggestionItems = (suggestions.data?.items ?? []).filter((s) => !existing.has(s.user.id));

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add members</DialogTitle>
          <DialogDescription>Add someone by email. If they aren&apos;t a user yet, you get an invite link to share.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3" noValidate>
          <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
            <div className="grid gap-1.5">
              <Label htmlFor="add-by-email">Email</Label>
              <Input
                id="add-by-email"
                type="email"
                autoComplete="off"
                placeholder="name@skaleminds.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? true : undefined}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-members-role">Role</Label>
              <RoleSelect id="add-members-role" value={role} onChange={setRole} />
            </div>
          </div>
          <Button type="submit" variant="outline" size="sm" disabled={addByEmail.isPending}>
            {addByEmail.isPending && pendingEmail === email.trim().toLowerCase() ? 'Adding…' : 'Add by email'}
          </Button>
          {error && (
            <p role="alert" className="text-sm text-danger-fg">
              {error}
            </p>
          )}
          {info && (
            <p role="status" className="text-sm text-muted-foreground">
              {info}
            </p>
          )}
        </form>

        <section className="space-y-2" aria-labelledby="add-members-suggestions">
          <p id="add-members-suggestions" className="text-xs font-medium text-muted-foreground">
            Add people who logged time here
          </p>
          {suggestions.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : suggestionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nobody else logged time here in the last {suggestions.data?.days ?? 90} days.</p>
          ) : (
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {suggestionItems.map((s) => (
                <li key={s.user.id} className="flex items-center gap-3 rounded-md px-2 py-1.5">
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">{s.user.name}</span>
                  <span className="font-mono text-xs tabular-nums text-muted-foreground">{formatDurationHm(s.seconds)}</span>
                  <Button
                    variant="ghost"
                    size="xs"
                    aria-label={`Add ${s.user.name}`}
                    disabled={addByEmail.isPending}
                    onClick={() => send(s.user.email)}
                  >
                    {pendingEmail === s.user.email ? 'Adding…' : 'Add'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {isAdmin && <AdminUserPicker projectId={projectId} existing={existing} role={role} onAdded={() => setInfo(null)} />}

        <DialogFooter>
          <Button variant="ghost" className="flex-1" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
